import axios from 'axios';
import React, { ReactNode, createContext, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SnowflakeCredentialsType } from '../types';
import { getBongoUrl } from '../../../common/utils/bongo';

interface SnowflakeContextType {
  tenant: number;
  editMode: boolean;
  setEditMode: (editMode: boolean) => void;
  getCredentials: () => Promise<SnowflakeCredentialsType | null>;
  credentials: SnowflakeCredentialsType | null;
  setCredentials: React.Dispatch<
    React.SetStateAction<SnowflakeCredentialsType | null>
  >;
  testCredentials: () => Promise<boolean>;
  getTables: () => Promise<string[]>;
  testTableMapping: () => Promise<boolean>;
  getColumns: () => Promise<Record<string, string[]>>;
  save: () => Promise<boolean>;
  clearCredentials: () => void;
}

export const SnowflakeContext = createContext<SnowflakeContextType | null>(
  null
);

export function SnowflakeProvider({
  tenant,
  children,
}: {
  tenant: number;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [
    credentials,
    setCredentials,
  ] = React.useState<SnowflakeCredentialsType | null>(null);
  const [editMode, setEditMode] = React.useState(false);

  async function getCredentials(): Promise<SnowflakeCredentialsType | null> {
    try {
      const response = await axios.get<SnowflakeCredentialsType>(
        `${getBongoUrl()}/v1/org/${tenant}/integrations/snowflake/credentials`
      );

      if (Object.keys(response.data || {}).length === 0) return null;

      let credsToSet = response.data;
      if (!credsToSet.authMethod && (credsToSet as any).pwd) {
        credsToSet = {
          ...credsToSet,
          authMethod: 'password',
        } as SnowflakeCredentialsType;
      }

      return credsToSet;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }

      throw error;
    }
  }

  async function getTables(): Promise<string[]> {
    const response = await axios.post<string[]>(
      `${getBongoUrl()}/v1/org/${tenant}/integrations/snowflake/get_tables`,
      credentials
    );
    return response.data;
  }

  async function getColumns(): Promise<Record<string, string[]>> {
    const response = await axios.post(
      `${getBongoUrl()}/v1/org/${tenant}/integrations/snowflake/get_columns`,
      credentials
    );

    return response.data;
  }

  async function testCredentials(): Promise<boolean> {
    try {
      const response = await axios.post(
        `${getBongoUrl()}/v1/org/${tenant}/integrations/snowflake/test_credentials`,
        credentials
      );

      return response?.data?.tested ?? false;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async function testTableMapping(): Promise<boolean> {
    try {
      const response = await axios.post(
        `${getBongoUrl()}/v1/org/${tenant}/integrations/snowflake/test_table_mapping`,
        credentials
      );

      return response?.data?.tested ?? false;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async function save(): Promise<boolean> {
    try {
      const response = await axios.post(
        `${getBongoUrl()}/v1/org/${tenant}/integrations/snowflake/credentials`,
        credentials,
        { params: { activate_pull: true } }
      );

      queryClient.setQueryData(
        ['snowflake', 'credentials', tenant],
        response.data
      );

      return true;
    } catch (error) {
      console.error(error);

      return false;
    }
  }

  function clearCredentials() {
    setCredentials(null);
    queryClient.invalidateQueries(['snowflake', 'credentials', tenant]);
  }

  return (
    <SnowflakeContext.Provider
      value={{
        tenant,
        editMode,
        setEditMode,
        getCredentials,
        credentials,
        setCredentials,
        testCredentials,
        getTables,
        testTableMapping,
        getColumns,
        save,
        clearCredentials,
      }}
    >
      {children}
    </SnowflakeContext.Provider>
  );
}

export function useSnowflakeContext() {
  const snowflakeContext = useContext(SnowflakeContext);

  if (!snowflakeContext)
    throw new Error(
      'useSnowflakeContext was used outside SnowflakeContext provider'
    );

  return snowflakeContext;
}
